import SchemaOrg from 'components/StructuredData/SchemaOrg';
import Layout from 'components/UI/Layout';
import SiteDataContext from 'context/SiteDataContext';
import useScript from 'hooks/useScript';
import { Board, getBoard, getJob, JobWithContent } from 'lib/greenhouse/api';
import boardClient from 'lib/greenhouse/client';
import { jobSchema } from 'lib/greenhouse/schema';
import {
  BrandedSiteProps,
  firstValue,
  getContentfulSiteData,
  getServerSidePropsWithErrors,
  isInvalid,
  notFound,
  withSiteData,
  InvalidResponse,
} from 'lib/routing';
import { ReactElement, useContext } from 'react';
import styles from './styles.module.css';
import axios from 'axios';

interface JobProps {
  preview: boolean;
  currentSlug: string;
  siteData: BrandedSiteProps;
  job: JobWithContent;
  board: Board;
}

interface WindowWithGreenhouse extends Window {
  readonly Grnhse: {
    Iframe: {
      load: (jobId: number) => void;
    };
  };
}

export default function Job({
  siteData,
  preview,
  currentSlug,
  job,
  board,
}: JobProps): ReactElement {
  const { domain } = useContext(SiteDataContext);
  const nextSeoProps = {
    description: `Put your future in focus with ${siteData.siteName} Learn more about our ${job.title} position in ${job.location}.`,
    title: `${job.title} in ${job.location}`,
  };

  const greenhouseOnLoad = (): void => {
    ((window as Window) as WindowWithGreenhouse).Grnhse.Iframe.load(job.id);
  };
  useScript(
    `https://boards.greenhouse.io/embed/job_board/js?for=${board.id}`,
    false,
    greenhouseOnLoad
  );

  return (
    <Layout
      siteData={siteData}
      preview={preview}
      nextSeoProps={nextSeoProps}
      currentSlug={currentSlug}
      pageDates={{
        datePublished: job.updated_at,
        dateModified: job.updated_at,
      }}
    >
      <SchemaOrg
        json={{
          '@context': 'https://schema.org',
          ...jobSchema(domain, job),
        }}
      />
      <div>
        <div className={styles.job_detail_wrapper}>
          <div className={styles.job_detail}>
            <div className={styles.job_detail_header}>
              <h1 className={styles.job_detail_title}>{`${job.title} Job in ${job.location}`}</h1>
              <span className={styles.job_detail_company_name}> at {board.name} </span>
              {/* <a className={styles.job_detail_link} href={pathWithoutQuery(router.asPath)}>
                (View all jobs)
              </a> */}
            </div>
            <div
              className={styles.job_detail_content}
              dangerouslySetInnerHTML={{ __html: unescape(job.content) }}
            ></div>
          </div>
        </div>
        <div id="grnhse_app"></div>
      </div>
    </Layout>
  );
}

export const getServerSideProps = getServerSidePropsWithErrors<JobProps>(
  async ({ resolvedUrl, res, req, params, preview }) => {
    // expecting {jobTitle}-{boardId}-{jobId}
    const [jobTitle, boardId, jobId] = firstValue(params?.job)?.split('-') || [];

    const canonicalRedirect = isRedirectNecessary(
      resolvedUrl.replace(/%2B/gi, '+'),
      firstValue(params?.location),
      jobTitle
    );
    if (canonicalRedirect) {
      return canonicalRedirect;
    }

    const siteData = await getContentfulSiteData({ req, res, resolvedUrl, preview });

    if (isInvalid(siteData)) {
      return siteData;
    }

    if (!boardId || !jobId) {
      return withSiteData(notFound(), siteData);
    }

    try {
      const [job, board] = await Promise.all([
        getJob(boardClient, boardId, jobId),
        getBoard(boardClient, boardId),
      ]);

      return {
        props: {
          preview: preview ? true : false,
          siteData,
          currentSlug: `careers/${params?.location}/${params?.job}`,
          job,
          board,
        },
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          return withSiteData(notFound(), siteData);
        }
      }

      return withSiteData(
        {
          props: {
            error: {
              message: (error as Error).message,
              httpCode: 500,
              type: 'Greenhouse API Error',
            },
          },
        },
        siteData
      );
    }
  }
);

/**
 * Returns a redirect to a canonicalized URL (location and job title are lowercase) if the
 * currently requested URL doesn't have a lowercase location and job title.
 */
function isRedirectNecessary(
  actualUrl: string,
  locationSegment: string | undefined,
  jobTitleSegment: string
): InvalidResponse | undefined {
  const canonicalUrl = actualUrl
    .replace(
      firstValue(locationSegment) || '',
      locationSegment ? firstValue(locationSegment).toLowerCase() : ''
    )
    .replace(jobTitleSegment, jobTitleSegment.toLowerCase());
  if (actualUrl !== canonicalUrl) {
    return {
      redirect: {
        statusCode: 301,
        destination: canonicalUrl,
      },
    };
  }
}

/**
 * Unescape HTML so we can render it in the component
 */
function unescape(input: string): string {
  return input
    .replace(/\u0026/g, '&')
    .replace(/&amp;nbsp;/g, '&nbsp;')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}
